import React, { useMemo, useState } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useTranslate } from 'react-admin';
import { CRM_COLORS, CRM_RADIUS } from '../../theme/crmColors';
import { BREAKPOINTS } from '../../theme/breakpoints';
import { toFormatDate, toFormatUtcDate } from '../../utils/date';
import { SourceBoardV2 } from '../../utils/types';
import identifyFormOrigin from './formOrigin';
import InstallationFiles from './InstallationFiles';
import InstallationSignatures from './InstallationSignatures';
import useInstallationFiles from './useInstallationFiles';
import useInstallationNotes from './useInstallationNotes';

const useStyles = makeStyles(() => ({
    panel: {
        display: 'grid',
        gap: 14,
        padding: '4px 20px 20px',
        backgroundColor: CRM_COLORS.surfaceMuted,
        borderTop: `1px solid ${CRM_COLORS.border}`,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: { padding: '4px 12px 12px' },
    },
    panelHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap' as const,
        borderBottom: `1px solid ${CRM_COLORS.border}`,
    },
    panelTitle: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        letterSpacing: '.06em',
        textTransform: 'uppercase' as const,
        color: CRM_COLORS.secondary,
        paddingRight: 8,
    },
    tabs: { display: 'flex', gap: 4, flexWrap: 'wrap' as const },
    tab: {
        height: 44,
        padding: '0 14px',
        border: 0,
        borderBottom: '3px solid transparent',
        marginBottom: -1,
        background: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: 'inherit',
        fontSize: '0.9375rem',
        fontWeight: 500,
        color: CRM_COLORS.text.secondary,
    },
    tabOn: {
        fontWeight: 700,
        color: CRM_COLORS.secondary,
        borderBottomColor: CRM_COLORS.success,
    },
    count: {
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '1px 8px',
        borderRadius: 999,
        backgroundColor: CRM_COLORS.status.neutral.background,
        color: CRM_COLORS.status.neutral.color,
        fontVariantNumeric: 'tabular-nums',
    },
    countOn: {
        fontWeight: 700,
        backgroundColor: CRM_COLORS.secondary,
        color: CRM_COLORS.surface,
    },
    subHead: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' as const },
    subTabs: {
        display: 'flex',
        gap: 4,
        padding: 4,
        borderRadius: 12,
        backgroundColor: '#e9eef3',
        flexWrap: 'wrap' as const,
    },
    subTab: {
        height: 40,
        padding: '0 14px 0 16px',
        border: 0,
        borderRadius: 9,
        background: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: 'inherit',
        fontSize: '0.875rem',
        fontWeight: 500,
        color: CRM_COLORS.text.label,
    },
    subTabOn: {
        backgroundColor: CRM_COLORS.surface,
        fontWeight: 700,
        color: CRM_COLORS.secondary,
        boxShadow: '0 1px 2px rgba(9, 43, 75, 0.12), 0 2px 6px rgba(9, 43, 75, 0.06)',
    },
    subCount: {
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '1px 7px',
        borderRadius: 999,
        backgroundColor: CRM_COLORS.surface,
        color: CRM_COLORS.text.label,
    },
    subCountOn: {
        fontWeight: 700,
        backgroundColor: CRM_COLORS.status.ok.background,
        color: CRM_COLORS.status.ok.color,
    },
    subHint: { fontSize: '0.875rem', color: CRM_COLORS.text.secondary },
    groups: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 16,
        alignItems: 'start',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    group: {
        minWidth: 0,
        backgroundColor: CRM_COLORS.surface,
        border: `1px solid ${CRM_COLORS.border}`,
        borderRadius: CRM_RADIUS.card,
        boxShadow: CRM_COLORS.shadow,
        overflow: 'hidden',
    },
    groupWide: { gridColumn: '1 / -1' },
    groupHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 18px',
        flexWrap: 'wrap' as const,
        backgroundColor: CRM_COLORS.surfaceMuted,
        borderBottom: `1px solid ${CRM_COLORS.borderSoft}`,
    },
    groupTitle: { fontSize: '1rem', fontWeight: 700, color: CRM_COLORS.secondary },
    originTag: {
        fontSize: '0.75rem',
        fontWeight: 500,
        padding: '2px 10px',
        borderRadius: 999,
        color: CRM_COLORS.primary,
        backgroundColor: CRM_COLORS.status.info.background,
    },
    formTag: {
        fontSize: '0.75rem',
        color: CRM_COLORS.text.secondary,
        fontVariantNumeric: 'tabular-nums',
    },
    personalTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        marginLeft: 'auto',
        padding: '3px 9px',
        borderRadius: 999,
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...CRM_COLORS.status.bad,
    },
    personalIcon: { fontSize: 13 },
    fields: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '16px 24px',
        padding: '16px 18px 18px',
    },
    fieldsWide: {
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    },
    field: { display: 'flex', flexDirection: 'column' as const, gap: 4, minWidth: 0 },
    label: { fontSize: '0.8125rem', color: CRM_COLORS.text.secondary, lineHeight: 1.3 },
    value: {
        fontSize: '0.875rem',
        color: CRM_COLORS.text.primary,
        lineHeight: 1.35,
        overflowWrap: 'anywhere' as const,
    },
    empty: { color: CRM_COLORS.text.secondary, fontStyle: 'italic' },
}));

/** A field with no value keeps its label: every group has the same shape on every installation. */
const Field = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
    const classes = useStyles();
    const isEmpty = value === null || value === undefined || `${value}`.trim() === '';
    return (
        <Box className={classes.field}>
            <Typography className={classes.label}>{label}</Typography>
            <Typography className={`${classes.value} ${isEmpty ? classes.empty : ''}`}>
                {isEmpty ? 'N/A' : value}
            </Typography>
        </Box>
    );
};

const Group = ({
    title,
    form,
    personal,
    wide,
    children,
}: {
    title: string;
    /** Takes the whole width, with four columns of fields. */
    wide?: boolean;
    /** Traceability form the values come from, e.g. _form3 — shown so a reader can trace a field back. */
    form?: string;
    /** Marks a group holding client/installer/distributor personal data (EF-2.4). */
    personal?: boolean;
    children: React.ReactNode;
}) => {
    const classes = useStyles();
    const t = useTranslate();
    return (
        <Box className={`${classes.group} ${wide ? classes.groupWide : ''}`}>
            <Box className={classes.groupHead}>
                <Typography className={classes.groupTitle} component='h3'>
                    {title}
                </Typography>
                {form && <span className={classes.formTag}>{form}</span>}
                {personal && (
                    <span className={classes.personalTag}>
                        <LockOutlinedIcon className={classes.personalIcon} aria-hidden />
                        {t('app.ancsV2.personalData')}
                    </span>
                )}
            </Box>
            <Box className={`${classes.fields} ${wide ? classes.fieldsWide : ''}`}>{children}</Box>
        </Box>
    );
};

const ANSWERS = ['YES', 'NO'];
const SOIL_OPTIONS = ['_FORM8', '_FORM9', '_FORM10'];

const dateOf = (value: string | null | undefined) => (value ? toFormatDate(value) : null);

type Tab = 'infos' | 'signatures' | 'files';
type SubTab = 'install' | 'coord' | 'recep';

const SUB_TABS: { id: SubTab; groups: number }[] = [
    { id: 'install', groups: 3 },
    { id: 'coord', groups: 4 },
    { id: 'recep', groups: 2 },
];
const GROUP_COUNT = SUB_TABS.reduce((total, { groups }) => total + groups, 0);

// Only the older process asks these on site, and some questions depend on the product. The current process dropped
// them: an empty row would be a question nobody was asked, so unlike the other fields they only appear when answered.
interface Row {
    field: keyof SourceBoardV2;
    label: string;
    kind?: 'answer' | 'soil';
}

const contactRows = (prefix: string): Row[] =>
    [
        ['Company', 'company'],
        ['LastName', 'lastName'],
        ['Address', 'address'],
        ['PostalCode', 'postalCode'],
        ['City', 'city'],
        ['Country', 'country'],
        ['Email', 'email'],
        ['PhoneNumber', 'phone'],
    ].map(([suffix, label]) => ({ field: `${prefix}${suffix}`, label: `app.ancsV2.forms.${label}` }));

const CLIENT_ROWS: Row[] = [
    { field: 'clientLastName', label: 'app.ancsV2.forms.lastName' },
    { field: 'clientName', label: 'app.ancsV2.forms.firstName' },
    { field: 'clientAddress', label: 'app.ancsV2.forms.address' },
    { field: 'clientPostalCode', label: 'app.ancsV2.forms.postalCode' },
    { field: 'clientCity', label: 'app.ancsV2.forms.city' },
    { field: 'clientCountry', label: 'app.ancsV2.forms.country' },
    { field: 'clientEmail', label: 'app.ancsV2.forms.email' },
    { field: 'clientPhoneNumber', label: 'app.ancsV2.forms.phone' },
];

const SITE_ROWS: Row[] = [
    { field: 'installationAddress', label: 'app.ancsV2.forms.address' },
    { field: 'installationPostalCode', label: 'app.ancsV2.forms.postalCode' },
    { field: 'installationCity', label: 'app.ancsV2.forms.city' },
    { field: 'installationCountry', label: 'app.ancsV2.forms.country' },
];

const SOIL_ROWS: Row[] = [
    { field: 'soilTypology', label: 'app.ancsV2.groups.soil', kind: 'soil' },
    { field: 'form8StableSoilBeddingGravel', label: 'app.ancsV2.forms.stableBedding', kind: 'answer' },
    { field: 'form8StableSoilSideBackfilling', label: 'app.ancsV2.forms.stableBackfilling', kind: 'answer' },
    { field: 'form9ImpermeableReinforcedSlab', label: 'app.ancsV2.forms.impermeableSlab', kind: 'answer' },
    { field: 'form9ImpermeableLeanConcreteBedding', label: 'app.ancsV2.forms.impermeableBedding', kind: 'answer' },
    { field: 'form9ImpermeableLateralBackfilling', label: 'app.ancsV2.forms.impermeableBackfilling', kind: 'answer' },
    { field: 'form10UnstableLeanConcreteBedding', label: 'app.ancsV2.forms.unstableBedding', kind: 'answer' },
    { field: 'form10UnstableLateralBackfilling', label: 'app.ancsV2.forms.unstableBackfilling', kind: 'answer' },
];

const RECEPTION_ROWS: Row[] = [
    { field: 'receptionQuoteNumber', label: 'app.ancsV2.forms.quoteNumber' },
    { field: 'receptionWorkInformation', label: 'app.ancsV2.forms.workInformation' },
    { field: 'receptionWithReserve', label: 'app.ancsV2.forms.withReserve', kind: 'answer' },
    { field: 'conditionsOfUseAndMaintenanceReceived', label: 'app.ancsV2.forms.conditionsReceived', kind: 'answer' },
    { field: 'receptionMadeIn', label: 'app.ancsV2.forms.madeIn' },
    { field: 'receptionDate', label: 'app.ancsV2.forms.madeOn' },
    { field: 'privacyPolicyAccepted', label: 'app.ancsV2.forms.privacyAccepted', kind: 'answer' },
];

const SITE_CHECKS: { field: keyof SourceBoardV2; label: string }[] = [
    { field: 'form7WaterRunoff', label: 'app.ancsV2.checks.waterRunoff' },
    { field: 'form7SteepSlope', label: 'app.ancsV2.checks.steepSlope' },
    { field: 'form7DevicesInWater', label: 'app.ancsV2.checks.devicesInWater' },
    { field: 'form7EvacuationAndVentilation', label: 'app.ancsV2.checks.evacuationAndVentilation' },
    { field: 'form7NoLoadWithin3Meters', label: 'app.ancsV2.checks.noLoadWithin3Meters' },
    { field: 'form7RapidRecovery', label: 'app.ancsV2.checks.rapidRecovery' },
    { field: 'form7NotInFloodProneArea', label: 'app.ancsV2.checks.notInFloodProneArea' },
    { field: 'form11WaterDecanter', label: 'app.ancsV2.checks.waterDecanter' },
    { field: 'form11BasketInPlace', label: 'app.ancsV2.checks.basketInPlace' },
    { field: 'form11DownstreamLiftingDevice', label: 'app.ancsV2.checks.downstreamLiftingDevice' },
    { field: 'form12BiologicalWaterReactor', label: 'app.ancsV2.checks.biologicalWaterReactor' },
    { field: 'form12GravityFlow', label: 'app.ancsV2.checks.gravityFlow' },
    { field: 'form12BiologicalFilterVentilation', label: 'app.ancsV2.checks.biologicalFilterVentilation' },
    { field: 'form13ClosedAndSecureBuffers', label: 'app.ancsV2.checks.closedAndSecureBuffers' },
    { field: 'form14OwnerInformed', label: 'app.ancsV2.checks.ownerInformed' },
];

/**
 * Unfolded card of the v2 board. Informations: the process dates and the form values, grouped the
 * way the forms are filled in. Signatures and Fichiers: what the history of the installation holds.
 */
const InstallationFormsPanel = ({ record }: { record?: SourceBoardV2 }) => {
    const t = useTranslate();
    const classes = useStyles();
    const [tab, setTab] = useState<Tab>('infos');
    const [sub, setSub] = useState<SubTab>('install');
    const { notes, loading, error } = useInstallationNotes(record?.avatarId);
    const { files, loading: loadingFiles, error: filesError } = useInstallationFiles(notes);
    const origin = useMemo(() => identifyFormOrigin(notes), [notes]);

    if (!record) {
        return null;
    }

    // The view stores the answers as codes (YES, _FORM8): show what the form offered instead.
    const answer = (value: string | null | undefined) =>
        value && ANSWERS.includes(value) ? t(`app.ancsV2.forms.answers.${value.toLowerCase()}`) : value;
    const soilOption = (value: string | null | undefined) =>
        value && SOIL_OPTIONS.includes(value)
            ? t(`app.ancsV2.forms.soilOptions.${value.replace('_', '').toLowerCase()}`)
            : value;

    const originLabel = () => {
        if (loading || error) {
            return null;
        }
        if (!origin.path) {
            return t('app.ancsV2.process.noForm');
        }
        return [origin.generation, origin.path]
            .filter(Boolean)
            .map((part) => t(`app.ancsV2.process.${part}`))
            .join(' · ');
    };

    // What was answered always shows. N/A is for what was asked and left empty. What this installation's process does
    // not ask disappears, and a group left with nothing says so rather than listing a column of N/A.
    const formGroup = (group: { title: string; form: string; personal?: boolean; wide?: boolean }, rows: Row[]) => {
        const asked = rows.filter(({ field }) => record[field] || origin.isAsked(String(field)));
        return (
            <Group {...group}>
                {asked.length > 0 ? (
                    asked.map(({ field, label, kind }) => {
                        const value = record[field] as string;
                        const shown = kind === 'answer' ? answer(value) : kind === 'soil' ? soilOption(value) : value;
                        return <Field key={String(field)} label={t(label)} value={shown} />;
                    })
                ) : (
                    <Typography className={`${classes.value} ${classes.empty}`}>
                        {t('app.ancsV2.forms.notAsked')}
                    </Typography>
                )}
            </Group>
        );
    };

    const siteForm = { older: '_form4', both: '_form3 · _form4' }[origin.generation as string] || '_form3';
    const siteChecks = SITE_CHECKS.filter(({ field }) => record[field]);
    const siteChecksGroup = siteChecks.length > 0 ? 1 : 0;
    const signed = [record.hasOwnerSignature, record.hasInstallerSignature].filter(Boolean).length;
    const tabs: { id: Tab; label: string; count: string | number }[] = [
        { id: 'infos', label: t('app.ancsV2.groups.information'), count: GROUP_COUNT + siteChecksGroup },
        { id: 'signatures', label: t('app.ancsV2.groups.signatures'), count: `${signed}/2` },
        { id: 'files', label: t('app.ancsV2.files.title'), count: files.length },
    ];

    return (
        <Box className={classes.panel}>
            <Box className={classes.panelHead}>
                <Typography className={classes.panelTitle}>
                    {record.productSerialNumber || 'N/A'} · {t('app.ancsV2.formsTitle')}
                </Typography>
                {originLabel() && <Typography className={classes.originTag}>{originLabel()}</Typography>}
                <Box className={classes.tabs} role='tablist' aria-label={t('app.ancsV2.formsTitle')}>
                    {tabs.map(({ id, label, count }) => (
                        <button
                            key={id}
                            type='button'
                            role='tab'
                            aria-selected={tab === id}
                            className={`${classes.tab} ${tab === id ? classes.tabOn : ''}`}
                            onClick={() => setTab(id)}
                        >
                            {label}
                            <span className={`${classes.count} ${tab === id ? classes.countOn : ''}`}>{count}</span>
                        </button>
                    ))}
                </Box>
            </Box>

            {tab === 'infos' && (
                <Box display='grid' gridGap={14} role='tabpanel' aria-label={t('app.ancsV2.groups.information')}>
                    <Box className={classes.subHead}>
                        <Box className={classes.subTabs} role='tablist' aria-label={t('app.ancsV2.groups.information')}>
                            {SUB_TABS.map(({ id, groups }) => (
                                <button
                                    key={id}
                                    type='button'
                                    role='tab'
                                    aria-selected={sub === id}
                                    className={`${classes.subTab} ${sub === id ? classes.subTabOn : ''}`}
                                    onClick={() => setSub(id)}
                                >
                                    {t(`app.ancsV2.subtabs.${id}`)}
                                    <span className={`${classes.subCount} ${sub === id ? classes.subCountOn : ''}`}>
                                        {groups + (id === 'install' ? siteChecksGroup : 0)}
                                    </span>
                                </button>
                            ))}
                        </Box>
                        <Typography className={classes.subHint}>{t(`app.ancsV2.subtabs.${sub}Hint`)}</Typography>
                    </Box>

                    <Box className={classes.groups}>
                        {sub === 'install' && (
                            <>
                                <Group wide title={t('app.ancsV2.groups.tracking')}>
                                    <Field
                                        label={t('app.ancsV2.columns.readyToDeliverDate')}
                                        value={dateOf(record.readyToDeliverDate)}
                                    />
                                    <Field label={t('app.ancsV2.columns.poseDate')} value={dateOf(record.poseDate)} />
                                    <Field
                                        label={t('app.ancsV2.columns.reserveLiftDate')}
                                        value={dateOf(record.reserveLiftDate)}
                                    />
                                    <Field
                                        label={t('app.ancs.installingDate')}
                                        value={dateOf(record.productInstallationDate)}
                                    />
                                    <Field
                                        label={t('app.ancsV2.columns.lastMaintenance')}
                                        value={
                                            record.productLastMaintenanceDate
                                                ? toFormatUtcDate(record.productLastMaintenanceDate)
                                                : null
                                        }
                                    />
                                    <Field
                                        label={t('app.ancsV2.columns.lastEmptying')}
                                        value={
                                            record.productLastEmptyingDate
                                                ? toFormatUtcDate(record.productLastEmptyingDate)
                                                : null
                                        }
                                    />
                                </Group>
                                <Group wide title={t('app.ancsV2.groups.product')} form='_form2'>
                                    <Field
                                        label={t('app.ancsV2.forms.invoiceDate')}
                                        value={record.form2DateInstallationOrInvoice}
                                    />
                                    <Field label={t('app.ancsV2.columns.model')} value={record.form2Model} />
                                    <Field
                                        label={t('app.ancsV2.forms.nominalCapacity')}
                                        value={record.form2NominalCapacity}
                                    />
                                    <Field
                                        label={t('app.ancsV2.forms.serialNumber')}
                                        value={record.form2SerialNumber}
                                    />
                                    <Field
                                        label={t('app.ancsV2.columns.processingCapacity')}
                                        value={record.processingCapacity}
                                    />
                                    <Field
                                        label={t('app.ancsV2.forms.sludgeStorageVolume')}
                                        value={record.sludgeStorageVolume}
                                    />
                                    <Field
                                        label={t('app.ancsV2.forms.totalUsefulVolume')}
                                        value={record.totalUsefulVolume}
                                    />
                                    <Field label={t('app.ancsV2.forms.postalCode')} value={record.productPostalCode} />
                                </Group>
                                {formGroup(
                                    { wide: true, title: t('app.ancsV2.groups.soil'), form: '_form8 · 9 · 10' },
                                    SOIL_ROWS,
                                )}
                                {siteChecks.length > 0 && (
                                    <Group wide title={t('app.ancsV2.groups.siteChecks')} form='_form7 · 11 · 12 · 13 · 14'>
                                        {siteChecks.map(({ field, label }) => (
                                            <Field key={field} label={t(label)} value={answer(record[field] as string)} />
                                        ))}
                                    </Group>
                                )}
                            </>
                        )}
                        {sub === 'coord' && (
                            <>
                                {formGroup(
                                    { personal: true, title: t('app.ancsV2.groups.client'), form: '_form3' },
                                    CLIENT_ROWS,
                                )}
                                {formGroup(
                                    { personal: true, title: t('app.ancsV2.groups.installation'), form: siteForm },
                                    SITE_ROWS,
                                )}
                                {formGroup(
                                    { personal: true, title: t('app.ancsV2.groups.installer'), form: '_form5' },
                                    contactRows('installer'),
                                )}
                                {formGroup(
                                    { personal: true, title: t('app.ancsV2.groups.distributor'), form: '_form6' },
                                    contactRows('distributor'),
                                )}
                            </>
                        )}
                        {sub === 'recep' && (
                            <>
                                {formGroup(
                                    { wide: true, title: t('app.ancsV2.groups.reception'), form: '_form15 · _form16' },
                                    RECEPTION_ROWS,
                                )}
                                <Group wide title={t('app.ancsV2.groups.reserves')} form='_form1'>
                                    <Field
                                        label={t('app.ancsV2.forms.kindOfReserve')}
                                        value={record.reserveKindOfReserve}
                                    />
                                    <Field
                                        label={t('app.ancsV2.forms.workToBePerformed')}
                                        value={record.reserveWorkToBePerformed}
                                    />
                                    <Field label={t('app.ancsV2.forms.workPeriod')} value={record.reserveWorkPeriod} />
                                    <Field label={t('app.ancsV2.forms.madeIn')} value={record.reserveMadeIn} />
                                    <Field label={t('app.ancsV2.forms.madeOn')} value={record.reserveDate} />
                                </Group>
                            </>
                        )}
                    </Box>
                </Box>
            )}

            {tab === 'signatures' && (
                <InstallationSignatures
                    notes={notes}
                    hasOwnerSignature={record.hasOwnerSignature}
                    hasInstallerSignature={record.hasInstallerSignature}
                    ownerName={record.clientFullName}
                    installerName={record.installerCompany}
                />
            )}

            {tab === 'files' && (
                <InstallationFiles files={files} loading={loading || loadingFiles} error={error || filesError} />
            )}
        </Box>
    );
};

export default InstallationFormsPanel;
